<template lang="pug">
v-container.pa-8(fluid)
  v-row.mb-2(no-gutters align="center")
    v-col.shrink
      v-btn.ml-n3(text icon @click="goBack" height)
        v-icon(color="neutral700" :size="24") mdi-arrow-left
    v-col.ml-3
      .text-2xl.font-semibold.neutral700--text Inventory Invoices
  v-row.pb-2.pb-md-0(no-gutters)
    v-col
      v-tabs(v-model="tabs" color="basic700" :height="44" :grow="isMobileScreen" active-class="font-semibold")
        v-tab.text-sm.font-medium.text-none.px-4 {{ $t('all') }}
        v-tab.text-sm.font-medium.text-none.px-4 Open
        v-tab.text-sm.font-medium.text-none.px-4 Overdue
        v-tab.text-sm.font-medium.text-none.px-4 Paid
    v-col
      .d-flex.align-center
        v-spacer
        v-btn(@click="clickSearch" icon color="neutral500")
          v-icon(size="20") $yzi-search-sm
        v-text-field(v-if="showSearchField" v-model="searchText" @blur="blurSearch" @input="queryChanged" solo dense flat :placeholder="$t('type_to_search')" hide-details clearable autofocus)
        div
          customize-column-menu(type="billing" :showTooltip="false" iconSize="21")

  v-expand-transition
    .d-flex.align-center.mt-4(v-if="checkedInvoices.length")
      v-btn.button-Aa-small.mr-3.px-3(@click="deleteMultiple" color="danger500" height=32 outlined depressed) {{ $t('delete') }}
      a.link-s(@click="clearSelection") {{ $t('clear_selection') }}

  filter-row.my-2(:filters.sync="filters" ref="filter")
  .data-table
    v-data-table.mt-sm-4.pb-6(:headers="headers" :items="invoices" item-key="id" :items-per-page="pageSize" hide-default-footer :loading="isFetching" :disable-sort="isMobileScreen" :header-props="{ sortIcon: '$yzi-triangle' }" @click:row="selectBill")
      template(v-slot:no-data)
        .mt-16
          v-img.mx-auto(src="@/assets/images/icon_empty.png" width=80)
          .mt-2.p2.basic600--text {{ $t('no_list_available') }}
      template(v-slot:header.checked)
        yc-checkbox(v-model="checkAll" @change="onCheckedAll")
      //- checked
      template(v-slot:item.checked="{ item }")
        div
          yc-checkbox(v-model="item.checked" @change="onChecked")
      //- amount
      template(v-slot:item.amount="{ item }")
        div.font-medium.neutral700--text {{ item.amount_total | displayPrice($currency) }}
      //- status
      template(v-slot:item.status="{ item }")
        template(v-if="item.amount_total > 0" )
          y-chip(v-if="item.paid" label="Paid" color="green")
            template(#prepend)
              v-icon.mr-1(size="12" color="success600") mdi-record
          template(v-else)
            y-chip(v-if="isOverdue(item)" label="Overdue" color="red")
              template(#prepend)
                v-icon.mr-1(size="12" color="danger600") mdi-record
            y-chip(v-else :label="getIntervalDays(item)" color="orange")
              template(#prepend)
                v-icon.mr-1(size="12" color="warning600") mdi-record
      //- unpaid balance
      template(v-slot:item.amount_remaining="{ item }")
        div.neutral700--text {{ item.amount_remaining | displayPrice($currency) }}
      //- invoice no
      template(v-slot:item.invoice_no="{ item }")
        div.neutral700--text {{ item.invoice_no }}
      //- seller
      template(v-slot:item.seller="{ item }")
        div.neutral700--text(v-if="item.seller") {{ item.seller.sold_by }}
      //- due date
      template(v-slot:item.due_at="{ item }")
        div.neutral700--text(v-if="item.due_at") {{ item.due_at | humanizeDate }}
      //- date issued
      template(v-slot:item.record_date="{ item }")
        div.neutral700--text(v-if="item.record_date") {{ item.record_date | humanizeDate }}
      //- actions
      template(v-slot:item.actions="{ item, index }")
        div(@click.stop)
          v-menu(offset-y :top="menuPositionTop(index)" left attach rounded="lg")
            template(#activator="{ on }")
              v-btn(icon small v-on="on")
                v-icon mdi-dots-horizontal
            v-list.py-0
              v-list-item(@click="editInvoice(item)")
                v-list-item-title.text-sm.basic800--text Edit invoice
              template(v-if="item.amount_remaining")
                v-divider
                v-list-item(@click="recordPayment(item)")
                  v-list-item-title.text-sm.basic800--text Record payment
              template
                v-divider
                v-list-item(@click="viewAttachment(item)")
                  v-list-item-title.text-sm.basic800--text View attachment
              v-divider
              v-list-item(@click="deleteInvoice(item)")
                v-list-item-title.text-sm.danger500--text Delete

  div.d-flex.align-center(v-if="totalCount > pageSize")
    v-spacer
    yc-pagination-custom(v-model="currentPage" :parentPageSize.sync="pageSize" :count="totalCount" attach menu-top @input="pageChanged")

  .dialogs
    invoice-attachment-dialog(v-if="showInvoiceAttachmentDialog" :open.sync="showInvoiceAttachmentDialog" :id="selectedInvoice.id" :invoice="selectedInvoice" @onUpdateCount="reset")
    record-invoice-payment-dialog(v-if="showRecordPaymentDialog" :open.sync="showRecordPaymentDialog" :id="selectedInvoice.id" :invoice="selectedInvoice" @onUploaded="reset")
    delete-confirmation-dialog(v-if="showDeleteDialog" :open.sync="showDeleteDialog" :loading="isLoading" @cancel="reset" @delete="confirmDelete")
      .text-lg.font-medium Are you sure you want to delete this invoice?
      .text-md.mt-4 This action will be done immediately and can’t be undone. Click “Confirm” to proceed
        
</template>

<script>
import { debounce } from '@/utils'
import { mapGetters, mapActions, mapState } from 'vuex'
import CustomizeColumnMenu from '@/components/connect/CustomizeColumnMenu.vue'
import FilterRow from '@/components/clinic/inventory/invoice/FilterRow.vue'
import InvoiceAttachmentDialog from '@/components/clinic/inventory/invoice/InvoiceAttachmentDialog.vue'
import RecordInvoicePaymentDialog from '@/components/clinic/inventory/invoice/RecordInvoicePaymentDialog.vue'
import DeleteConfirmationDialog from '@/components/clinic/dialogs/DeleteConfirmationDialog.vue'

export default {
  name: 'InventoryInvoices',
  props: {},
  components: { CustomizeColumnMenu, FilterRow, InvoiceAttachmentDialog, RecordInvoicePaymentDialog, DeleteConfirmationDialog },
  data: () => ({
    isLoading: false,
    isFetching: false,
    showDetails: false,
    checkAll: false,
    showDeleteDialog: false,
    showSearchField: false,
    openUpdateClaimDialog: false,
    openSelectInvoiceDialog: false,
    openSelectReceiptDialog: false,
    showInvoiceAttachmentDialog: false,
    showRecordPaymentDialog: false,
    selectedInvoice: null,
    searchText: '',
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    tabs: 0,
    invoiceTemplates: [],
    invoices: [],
    selectedInvoices: [],
    selectedTransactions: [],
    checkedInvoices: [],
    filters: [],
  }),
  computed: {
    ...mapState('user', ['user', 'member']),
    ...mapGetters({
      company: 'getCompany',
      fields: 'clinic/getInventoryInvoiceFields'
    }),
    headers() {
      const filteredOptions = this.fields.filter(item => item.enable)
      const checkbox = {
        text: '',
        value: 'checked',
        sortable: false
      }
      const amount = {
        text: 'Amount',
        value: 'amount',
        class: 'text-table-header'
      }
      filteredOptions.map(item => {
        item.text = item.field_name
        item.value = `${item.id}`.toLowerCase()
        item['class'] = 'text-table-header'
      })
      const actions = {
        text: '',
        value: 'actions',
        class: 'text-table-header'
      }
      return [checkbox, amount, name, ...filteredOptions, actions]
    },
    isMobileScreen() {
      return this.$vuetify.breakpoint.xs
    }
  },
  watch: {
    tabs () {
      this.searchText = null
      this.currentPage = 1
      this.$refs.filter.resetFilters()
      this.fetchInventoryInvoices()
    },
    pageSize() {
      this.currentPage = 1
      this.fetchInventoryInvoices()
    },
    filters () {
      this.currentPage = 1
      this.fetchInventoryInvoices()
    },
  },
  created () {
    this.checkRoute()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchInventoryInvoices () {
      this.isFetching = true
      const params = {
        page_size: this.pageSize,
        page: this.currentPage,
        ordering: '-record_date'
      }
      if (this.tabs === 1) { params.status = 'open' }
      if (this.tabs === 2) { params.status = 'overdue' }
      if (this.tabs === 3) { params.status = 'paid' }
      if (this.searchText) { params.search = this.searchText }
      this.filters.forEach(filter => {
        params[filter.key] = filter.value
      })
      this.$axios.get(`/clinics/${this.company.slug}/inventory_invoices/`, { params })
        .then(resp => {
          const data = resp.data
          this.invoices = data.results
          this.invoices.map(item => {
            item.checked = false
          })
          this.totalCount = resp.data.count
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false
        })
    },
    pageChanged() {
      this.fetchInventoryInvoices()
    },
    queryChanged: debounce(function () {
      this.currentPage = 1
      this.fetchInventoryInvoices()
    }, 750),
    blurSearch () {
      if (!this.searchText || this.searchText.length === 0) {
        this.showSearchField = false
      }
    },
    clickSearch () {
      if (this.showSearchField) {
        if (!this.searchText || this.searchText.length === 0) {
          this.showSearchField = false
        }
      } else {
        this.showSearchField = true 
      }
    },
    selectBill (data) {
      // data.checked = !data.checked
      // this.onChecked()
      this.editInvoice(data)
    },
    editInvoice (data) {
      this.$router.push(`/admin/clinic/inventory-invoice/${data.id}`)
    },
    onChecked() {
      this.checkedInvoices = this.invoices.filter(item => item.checked === true)
      this.invoices = [...this.invoices]
      this.checkAll = false
    },
    onCheckedAll() {
      const clone = [...this.invoices]
      clone.map(item => item.checked = this.checkAll)
      this.invoices = [...clone]
      this.checkedInvoices = this.invoices.filter(item => item.checked === true)
    },
    clearSelection() {
      this.checkAll = false
      this.onCheckedAll()
    },
    reset () {
      this.clearSelection()
      this.currentPage = 1
      this.fetchInventoryInvoices()
      this.closed()
    },
    closed () {
      this.selectedInvoices = []
      this.showInvoiceAttachmentDialog = false
      this.showRecordPaymentDialog = false
      this.showDeleteDialog = false
      this.selectedInvoice = null
    },
    recordPayment (item) {
      this.selectedInvoice = item
      this.showRecordPaymentDialog = true
    },
    viewAttachment (item) {
      this.selectedInvoice = item
      this.showInvoiceAttachmentDialog = true
    },
    deleteInvoice (item) {
      this.selectedInvoices.push(item)
      this.showDeleteDialog = true
    },
    deleteMultiple () {
      this.selectedInvoices = this.checkedInvoices
      this.showDeleteDialog = true
    },
    async confirmDelete () {
      this.isLoading = true
      try {
        for (const invoice of this.selectedInvoices) {
          await this.$axios.delete(`/clinics/${this.company.slug}/inventory_invoices/${invoice.id}`)
        }
        this.reset()
      } catch { 
        this.showMessage(['error', this.$t('error_message.error_occured')])
      } finally {
        this.isLoading = false
      }
    },
    getTodayDate () {
      const today = new Date()
      const year = today.getFullYear()
      const month = String(today.getMonth() + 1).padStart(2, '0')
      const day = String(today.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    checkRoute() {
      if (this.$route.query.tab && Number(this.$route.query.tab) !== 0) {
        this.tabs = Number(this.$route.query.tab)
        return
      }
      this.fetchInventoryInvoices()
    },
    isOverdue (item) {
      if (!item.due_at) {
        return false
      }
      let dueDate = new Date(item.due_at)
      let today = new Date(this.getTodayDate())
      if (today > dueDate) {
        return true
      } else {
        return false
      }
    },
    getIntervalDays (item) {
      if (!item.due_at) {
        return 'Open'
      }
      let dueDate = new Date(item.due_at)
      let today = new Date(this.getTodayDate())
      const timeDifference = dueDate - today;
      const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24))
      return `Due in ${Math.abs(daysDifference)} days`
    },
    menuPositionTop (index) {
      let position = index + 1
      if (position > 7) {
        let start = this.invoices.length - 3
        if (position > start) {
          return true
        }
      }
      return false
    },
    goBack () {
      this.$router.push('/admin/clinic/settings')
    },
  }
}
</script>

<style scoped>
::v-deep .v-data-table__wrapper table tbody tr:hover{
  cursor: pointer;
  background: var(--v-neutral200) !important; 
}
.ellipsis-text {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
</style>